import React, { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Zap, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import TurnstileWidget from "@/components/TurnstileWidget";

const PRODUCT_NAME = "Production Pay Tracker";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [resending, setResending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const onToken = useCallback((token) => setCaptchaToken(token), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerification(false);
    setLoading(true);
    const res = await login(email, password, captchaToken);
    setLoading(false);
    if (res.ok) {
      const u = res.user;
      const greetingName = u.first_name || u.name || (u.email ? u.email.split("@")[0] : "back");
      toast.success(`Welcome, ${greetingName}`);
      const isAdminRole = res.user.role === "admin" || res.user.role === "super_admin";
      navigate(isAdminRole ? "/admin" : "/");
    } else if (res.code === "email_not_verified") {
      setNeedsVerification(true);
      setError(res.error);
    } else {
      setError(res.error);
      // Captcha tokens are single-use — after any rejection, fetch a fresh
      // one so the next submit isn't dead on arrival.
      setCaptchaToken(null);
      captchaRef.current?.reset();
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Verification email re-sent. Check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="login-page">
      {/* Visual side */}
      <div
        className="hidden md:flex md:flex-1 relative items-center justify-center text-white p-12"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(2,132,199,0.85) 0%, rgba(2,72,199,0.95) 100%), url('https://images.pexels.com/photos/36525955/pexels-photo-36525955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-14 w-14 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center overflow-hidden">
              <Zap className="h-7 w-7" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest opacity-80">Smarter Pay. Stronger Teams.</p>
              <p className="text-2xl font-bold font-display tracking-tight">{PRODUCT_NAME}</p>
            </div>
          </div>
          <h2 className="text-4xl font-extrabold font-display tracking-tighter mb-4">
            Clock in. Submit jobs. Get paid.
          </h2>
          <p className="text-white/85 text-lg leading-relaxed">
            A simple, accurate time & commission tracker built for the field. Capture time,
            allowable deductions, and digital signatures — then export weekly payroll in one click.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-4 text-center">
            {[
              ["Time", "tracked to the minute"],
              ["GPS", "location verified"],
              ["Payroll", "auto-calculated"],
            ].map(([k, v]) => (
              <div key={k} className="border border-white/20 rounded-xl p-3 backdrop-blur-sm bg-white/5">
                <p className="text-sm font-bold">{k}</p>
                <p className="text-xs opacity-80">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center overflow-hidden">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500">Smarter Pay. Stronger Teams.</p>
              <p className="text-xl font-bold font-display tracking-tight text-slate-900">{PRODUCT_NAME}</p>
            </div>
          </div>

          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mb-2">Sign in to Production Pay Tracker</h1>
          <p className="text-slate-500 mb-8">Welcome back. Sign in to clock in or manage payroll.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@yourcompany.com"
                className="h-12 mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-sky-700 hover:text-sky-800"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="h-12 pr-12"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-slate-500 hover:text-slate-900 focus:outline-none focus:text-sky-700"
                  data-testid="login-password-toggle-btn"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="login-error">
                {error}
                {needsVerification && (
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resend}
                      disabled={resending}
                      className="bg-white"
                      data-testid="login-resend-verification-btn"
                    >
                      {resending ? "Sending…" : "Resend verification email"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <TurnstileWidget ref={captchaRef} action="login" onToken={onToken} className="pt-1" />

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base font-bold bg-sky-600 hover:bg-sky-700"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>

            <p className="text-center text-sm text-slate-600 pt-2">
              New to Production Pay Tracker?{" "}
              <Link to="/signup" className="font-bold text-sky-700 hover:text-sky-800" data-testid="login-signup-link">
                Create an account
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
